import { useState } from "react";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { FileThumbnail } from "@/components/files/FileThumbnail";
import { usePermissions } from "@/hooks/use-permissions";
import { artworkTemplatesApi, useArtworkTemplates, type ArtworkTemplateListRow } from "@/lib/artwork-templates";

// The list is small and unpaginated server-side; DataGrid still requires page
// props, so we page client-side over the full result with a page size large
// enough that a single page always covers it.
const PAGE_SIZE = 200;

export function ArtworkTemplatesAdminPage() {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { has } = usePermissions();
  const { data, isLoading } = useArtworkTemplates();
  const rows = data ?? [];

  const columns: DataGridColumn<ArtworkTemplateListRow>[] = [
    { id: "name", header: "Name", accessor: (r) => r.name },
    {
      id: "datasheet",
      header: "Datasheet",
      accessor: (r) => [r.companyName, r.datasheetTypeDescription].filter(Boolean).join(" — ") || `#${r.datasheetId}`,
    },
    { id: "size", header: "Prepaid size", accessor: (r) => r.prepaidSize, align: "right" },
    { id: "mult", header: "Multiple", accessor: (r) => r.prepaidSizeMultiple, align: "right" },
    { id: "fields", header: "Fields", accessor: (r) => r.fieldCount, align: "right" },
    {
      id: "img",
      header: "Image",
      accessor: (r) =>
        r.hasImage ? (
          <FileThumbnail
            url={r.hasThumbnail ? artworkTemplatesApi.thumbnailUrl(r.artworkTemplateId) : null}
            contentType="image/png"
            alt={r.name}
          />
        ) : (
          "—"
        ),
    },
  ];

  return (
    <ListPage
      title="Artwork Templates"
      backTo="/admin"
      primaryAction={
        has("artwork-templates:write") ? (
          <IntentButton onClick={() => void navigate("/admin/artwork-templates/new")}>
            <Plus className="mr-2 h-4 w-4" /> New template
          </IntentButton>
        ) : undefined
      }
    >
      <DataGrid
        columns={columns}
        rows={rows}
        rowKey={(r) => r.artworkTemplateId}
        totalRows={rows.length}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        emptyHeadline="No artwork templates yet."
        onPageChange={setPage}
        onRowClick={(r) => void navigate(`/admin/artwork-templates/${r.artworkTemplateId}`)}
      />
    </ListPage>
  );
}
